import type { InjectionProtocol } from "@src/packages/injection-protocol/types";

import {
  ADMINISTRATION_METHODS,
  methodIsOffered,
  methodsFor,
} from "./deviceChoices";

/**
 * Which device she is injecting with, decided once for every surface.
 *
 * There are two doors into the coach — a medication row, and the trigger-shot
 * step of the care hub — and for a while they answered this question
 * differently. One showed her what her calendar recorded and let her confirm or
 * correct it; the other went straight to a list of buttons, offered no way to
 * say "none of these", and forgot her answer the moment she closed it. Same
 * patient, same medication, two behaviours, decided by where she tapped.
 *
 * So the decision lives here and both doors render it. That is also what makes
 * it testable: it was a chain of ternaries inside a component, where falling
 * through a branch produces no error at all — which is exactly how a patient on
 * Pregnyl reached the route question after saying the device was not hers, and
 * how a combined trigger came to loop forever between its two injections.
 */

export type DeviceDecisionStage =
  /** Show her what the walk assumes, to confirm or to change. */
  | "confirm"
  /** Ask which of these she has. */
  | "choose"
  /** Say we have no steps for the device she has. */
  | "unguided"
  /** Nothing to ask; `decided` is the answer, which may be null. */
  | "settled";

export type DeviceDecisionInput = {
  /** Every procedure that answers to the medication she opened. */
  protocols: readonly InjectionProtocol[];
  /** The administration method her calendar records, untranslated. */
  recordedForm: string | null;
  /** What she has picked from the buttons this session. */
  answered: string | null;
  /** She has agreed with the proposal. */
  confirmed: boolean;
  /** She has said the proposal is not what she has. */
  declined: boolean;
  /** She has said she has none of the ones offered. */
  noneOfThese: boolean;
};

export type DeviceDecision = {
  stage: DeviceDecisionStage;
  /**
   * What the walk assumes, for the confirmation card.
   *
   * Her record where she has one — **whether or not we can walk it**, because
   * the likeliest reason it names a device this medication is not sold as is
   * that the field was filled in quickly or by an import, and she should be
   * given the chance to say so before being told there is nothing for it.
   * Otherwise the medication's own, where it comes exactly one way.
   */
  proposed: string | null;
  /** Whether a walk exists for the proposal. Confirming one that has none
   * reaches `unguided`, after she has had the chance to correct it. */
  proposalIsGuided: boolean;
  /** The buttons to offer, never including one she has already refused. */
  methods: readonly string[];
  /** The method to narrow the procedures by, once there is one. */
  decided: string | null;
};

export function deviceDecision({
  protocols,
  recordedForm,
  answered,
  confirmed,
  declined,
  noneOfThese,
}: DeviceDecisionInput): DeviceDecision {
  const offered = methodsFor(protocols);
  // Nothing to walk at all. The entry point is supposed to have caught this —
  // see `resolveCoachAvailability` — but it answers on a looser name match than
  // the walk does, and a medication sold only outside her market resolves to an
  // empty list here while the button was still shown. Without this the empty
  // list reached `settled` with nothing decided, and the walk opened blank.
  if (protocols.length === 0) {
    return {
      stage: "unguided",
      proposed: null,
      proposalIsGuided: false,
      methods: [],
      decided: null,
    };
  }
  // A device we cannot name cannot be shown to her. The API's `form` enum has
  // fourteen values and five are administration methods her own form writes;
  // the rest have no button and therefore no label, and `other` — which is what
  // "Multi-flacons" is stored as — is the one she can actually have. Proposing
  // it produced a card reading "This medication comes as “”". Unnameable is
  // treated as unrecorded: she is asked, which is what an ambiguous value
  // deserves.
  const namedRecord =
    recordedForm &&
    (ADMINISTRATION_METHODS as readonly string[]).includes(
      recordedForm.trim().toLowerCase(),
    )
      ? recordedForm.trim().toLowerCase()
      : null;
  const proposed =
    namedRecord ?? (offered.length === 1 ? (offered[0] ?? null) : null);
  const proposalIsGuided = methodIsOffered(protocols, proposed);
  // Confirming a device we have no steps for settles nothing: she answered
  // truthfully about her box, and it is our record of it that was wrong.
  const decided = answered ?? (confirmed && proposalIsGuided ? proposed : null);
  // The same press, read as "not that one", when the thing she confirmed is
  // something this medication is not dispensed as. She answered truthfully; it
  // is our record of her box that was wrong.
  const refused = declined || (confirmed && !proposalIsGuided);

  // Without the one she has refused — by saying so, or by confirming something
  // we cannot walk. Not always a shorter list: her record may have named
  // something this medication is not dispensed as at all, in which case there
  // is still a real alternative to show her.
  const methods =
    refused && proposed
      ? offered.filter((method) => method !== proposed)
      : offered;

  // She has nothing we can walk. Either she said so, or she refused the only
  // thing there was to refuse.
  if (noneOfThese || (refused && methods.length === 0)) {
    return { stage: "unguided", proposed, proposalIsGuided, methods, decided };
  }

  // Before anything else: what does the walk think she has?
  if (!confirmed && !declined && answered === null && proposed !== null) {
    return { stage: "confirm", proposed, proposalIsGuided, methods, decided };
  }

  // She refused, and there is something else to offer. Asked here rather than
  // left to the narrowing downstream, which only asks when two procedures
  // disagree about the device — a medication that comes one way has no
  // disagreement, so her refusal used to fall through it silently.
  if (decided === null && (refused || offered.length > 1)) {
    return { stage: "choose", proposed, proposalIsGuided, methods, decided };
  }

  return { stage: "settled", proposed, proposalIsGuided, methods, decided };
}
